import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import PhotoPlaceholder from "../components/PhotoPlaceholder";
import AerialShowcase from "../components/AerialShowcase";
import Seo from "../components/Seo";
import { Reveal } from "../components/Motion";
import { GALLERY_CATEGORIES, CRAFTSMANSHIP } from "../data/site";
import { useSocial, activePublic, SocialIconRow } from "../components/SocialLinks";

export default function Gallery() {
  const social = useSocial();
  const socialLinks = activePublic(social);
  const ctaPlatform = social?.gallery_cta_platform || "instagram";
  const ctaLink = socialLinks.find((s) => s.id === ctaPlatform) || socialLinks[0];
  // Self-healing gallery: if any image fails to load, its card is removed
  // entirely — a broken-image icon is never shown to a homeowner.
  const [failed, setFailed] = useState([]);
  const hideOnError = (src) => setFailed((f) => (f.includes(src) ? f : [...f, src]));
  const ok = (src) => src && !failed.includes(src);
  const [filter, setFilter] = useState("all");
  const visibleCats = GALLERY_CATEGORIES.filter((c) => c.items.some((i) => ok(i.src)));
  const catsToShow = GALLERY_CATEGORIES.filter((cat) => filter === "all" || cat.id === filter);
  return (
    <div data-testid="gallery-page" className="pt-28 bg-white">
      <Seo
        title="Project Gallery — Real Zarco's Decks Builds | North Georgia"
        description="Browse real Zarco's Decks projects: composite decks, wood decks, covered porches, stairs and railings, plus aerial drone views of completed North Georgia outdoor living spaces."
      />
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-24">
        <SectionHeading
          as="h1"
          overline="Project Gallery"
          title="Real Zarco's Decks projects across Northwest Georgia."
          subtitle="A curated look at recent decks, covered porches, and outdoor living builds. New projects are added as they're completed and photographed."
        />
        {/* Browse by project type — only categories with real content appear */}
        <div data-testid="gallery-filters" className="mt-10 flex flex-wrap gap-2.5">
          {[{ id: "all", title: "All Projects" }, ...visibleCats].map((c) => (
            <button
              key={c.id}
              onClick={() => setFilter(c.id)}
              data-testid={`gallery-filter-${c.id}`}
              className={`px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold border transition-colors ${
                filter === c.id
                  ? "bg-[#111] text-white border-[#111]"
                  : "bg-white text-[#2A2A2A] border-[#E7E2D9] hover:border-[#99582A] hover:text-[#99582A]"
              }`}
            >
              {c.title}
            </button>
          ))}
        </div>
      </section>

      {catsToShow.map((cat, ci) => {
        const items = cat.items.filter((item) => ok(item.src));
        if (items.length === 0) return null; // never render an empty category
        const twoUp = items.length === 2;
        return (
        <section
          key={cat.id}
          data-testid={`gallery-section-${cat.id}`}
          className={`px-6 md:px-12 lg:px-20 py-20 md:py-24 ${
            ci % 2 === 1 ? "bg-[#FBF8F3]" : "bg-white"
          }`}
        >
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-10">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
                0{ci + 1} — Category
              </p>
              <h2 className="font-display text-3xl md:text-4xl tracking-tight font-medium mt-3 text-[#111]">
                {cat.title}
              </h2>
              <p className="mt-4 text-[#4A4A4A] max-w-2xl leading-relaxed">
                {cat.description}
              </p>
            </div>
            <div className="lg:col-span-5 lg:text-right text-xs uppercase tracking-[0.25em] font-semibold text-[#666]">
              {items.length} project photo{items.length === 1 ? "" : "s"}
            </div>
          </div>

          <div className={twoUp ? "grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5" : "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"}>
            {items.map((item, i) => (
              <Reveal
                key={`${cat.id}-${i}`}
                delay={(i % 4) * 0.06}
                y={20}
                data-testid={`gallery-${cat.id}-item-${i}`}
                className={twoUp
                  ? "img-hover img-spotlight relative overflow-hidden aspect-[4/3]"
                  : `img-hover img-spotlight relative overflow-hidden ${
                      i === 0
                        ? "col-span-2 md:col-span-2 md:row-span-2 aspect-[4/5]"
                        : "aspect-[3/4]"
                    }`}
              >
                {item.src ? (
                  <>
                    <img
                      src={item.src}
                      alt={item.label}
                      loading="lazy"
                      decoding="async"
                      onError={() => hideOnError(item.src)}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ objectPosition: item.focal || "center" }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                    <span className="absolute bottom-3 left-3 md:bottom-5 md:left-5 text-white text-[11px] md:text-sm font-semibold tracking-wide">
                      {item.label}
                    </span>
                  </>
                ) : (
                  <PhotoPlaceholder
                    label={item.label}
                    sublabel="Photo coming soon"
                    aspect={twoUp ? "4/3" : i === 0 ? "4/5" : "3/4"}
                    variant={i === 0 ? "wood" : "dark"}
                  />
                )}
              </Reveal>
            ))}
          </div>
        </section>
        );
      })}

      {/* CRAFTSMANSHIP — replaces the fabricated before/after section (shown in "All" view) */}
      {filter === "all" && (
      <section
        data-testid="gallery-section-craftsmanship"
        className="px-6 md:px-12 lg:px-20 py-20 md:py-24 bg-white"
      >
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
              05 — {CRAFTSMANSHIP.overline}
            </p>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight font-medium mt-3 text-[#111]">
              {CRAFTSMANSHIP.title}
            </h2>
            <p className="mt-4 text-[#4A4A4A] leading-relaxed">
              {CRAFTSMANSHIP.body}
            </p>
            <p className="mt-4 text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">
              Build in progress · Real jobsite
            </p>
          </div>
          <div className="lg:col-span-7">
            {ok(CRAFTSMANSHIP.image) && (
              <>
                <div className="img-hover aspect-[4/5] md:aspect-[16/11] w-full">
                  <img
                    src={CRAFTSMANSHIP.image}
                    alt={CRAFTSMANSHIP.caption}
                    loading="lazy"
                    decoding="async"
                    onError={() => hideOnError(CRAFTSMANSHIP.image)}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: CRAFTSMANSHIP.focal }}
                  />
                </div>
                <p className="mt-3 text-xs text-[#666] italic">{CRAFTSMANSHIP.caption}</p>
              </>
            )}
          </div>
        </div>
      </section>
      )}

      {/* AERIAL / DRONE SHOWCASE — CMS-driven, real Zarco's media (shown in "All" view) */}
      {filter === "all" && <AerialShowcase />}

      {/* SOCIAL CTA — subtle "See More of Our Work"; only renders when a social link exists */}
      {socialLinks.length > 0 && (
        <section
          data-testid="gallery-social-cta"
          className="px-6 md:px-12 lg:px-20 py-20 md:py-24 bg-[#FBF8F3] border-t border-[#E7E2D9]"
        >
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <Reveal className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
                Follow Along
              </p>
              <h2 className="font-display text-3xl md:text-4xl tracking-tight font-medium mt-3 text-[#111]">
                See More of Our Work
              </h2>
              <p className="mt-4 text-[#4A4A4A] leading-relaxed max-w-2xl">
                This gallery is our curated portfolio. For the full stream — recent builds,
                before &amp; after transformations, project progress, finished decks, reels,
                drone footage, and behind-the-scenes construction — follow Zarco's on social.
              </p>
              <div className="mt-7">
                <SocialIconRow social={social} testidPrefix="gallery-social" />
              </div>
            </Reveal>
            {ctaLink && (
              <Reveal delay={0.1} className="lg:col-span-5 lg:flex lg:justify-end">
                <a
                  href={ctaLink.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="gallery-social-cta-btn"
                  className="group inline-flex items-center gap-3 bg-[#111] text-white hover:bg-[#99582A] transition-colors px-8 py-4 font-semibold tracking-wide"
                >
                  Follow on {ctaLink.label === "Reviews" ? "Google" : ctaLink.label}
                  <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </Reveal>
            )}
          </div>
        </section>
      )}

      <section className="bg-[#2A2A2A] text-white px-6 md:px-12 lg:px-20 py-20">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight font-medium">
              Want your project featured here next?
            </h2>
            <p className="mt-3 text-white/85 max-w-xl">
              Free estimates. Honest timelines. Premium craftsmanship across
              Metro Atlanta & North Georgia.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="gallery-cta-free-estimate-btn"
            className="bg-[#C27A3E] text-white hover:bg-white hover:text-[#111] transition-colors px-8 py-4 font-semibold tracking-wide inline-flex items-center gap-3"
          >
            Get Your Free Estimate <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
